/**
 * Adapts a `@tursodatabase/sync-react-native` `Database` to look like an
 * expo-sqlite `SQLiteDatabase`.
 *
 * Why this exists: Turso's bidirectional sync captures local writes via a
 * per-connection CDC log (`turso_cdc`) — only writes made THROUGH the Turso
 * `Database` connection are pushed to the remote. The actor DB therefore has to
 * run ALL its queries through this one Turso connection (not a separate
 * handle), or nothing it writes ever syncs. This adapter bridges the
 * project's drizzle `expo-sqlite-async` driver, `createPerformanceLogger`, and
 * direct PRAGMA callers onto the Turso `Database` API (`run` / `all` / `get` /
 * `exec` / `prepare` / `transaction`), so the entire actor data layer keeps
 * speaking the expo-sqlite shape while writes flow through Turso's CDC.
 *
 * Change-event emission does NOT live here: the connection is wrapped with
 * `attachSqliteChangeFeed` at open time (see `actor-database.ts`), which emits
 * on every mutating statement uniformly across engines — including the
 * `INSERT … RETURNING`-via-`getAllAsync` path this adapter's old per-method
 * emission missed.
 *
 * It also serializes EVERY statement (and every drizzle transaction, held across
 * `begin` … `commit`) through a connection-scoped `ActorSyncConnectionLock` that
 * `pull()` / `push()` share — see `actor-sync-connection-lock.ts`. Without this an
 * app write transaction's `begin` could land while a `pull()` is mid-`applyChanges`
 * and the engine rejects it ("cannot start a transaction within a transaction"),
 * which drizzle surfaces as the "Could not save your progress / Failed query:
 * begin" toast.
 */
import { splitSqlStatements, type SQLiteDatabase } from "@-/com.npmjs.expo-sqlite";
import type {
  Database as TursoDatabase,
  BindParams,
} from "@-/com.npmjs.tursodatabase__sync-react-native";
import {
  classifyTransactionControl,
  HOLD_DEADLINE_MS,
  requiresTransactionOwner,
  type ActorSyncConnectionLock,
  type LockRelease,
} from "#actor-sync-connection-lock";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import {
  actorBackupTableSql,
  type ActorBackupRestoreOptions,
  type ActorBackupRestoreStatement,
} from "#actor-backup-reader";

function normalizeParams(params: unknown): BindParams[] {
  if (params == null || !Array.isArray(params)) {
    return [];
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- bind params validated as an array before Turso bind
  return params as BindParams[];
}

interface TursoAdapterStatement {
  executeAsync(params?: unknown): Promise<{
    changes: number;
    getAllAsync(): Promise<unknown[]>;
    getFirstAsync(): Promise<unknown>;
    lastInsertRowId: number;
  }>;
  executeForRawResultAsync<T>(params?: unknown): Promise<{ getAllAsync(): Promise<T[]> }>;
  finalizeAsync(): Promise<void>;
}

/**
 * Marks an engine whose own `close()` waits for every native call still running inside it before
 * closing natively (the owned native generation, `open-owned-turso-generation.ts`). Only such an
 * engine may be closed after its connection lock was poisoned: the holder that overran may still be
 * inside the engine, and a plain engine closed under it is a use-after-free. Every other engine is
 * abandoned instead.
 */
export const DRAINS_IN_FLIGHT_ON_CLOSE = Symbol.for("han.turso.drainsInFlightOnClose");

/**
 * A backup restore is one exclusive slot for the whole restore (every row of a study database), far
 * longer than one statement. 3 minutes covers a large restore on a slow device; past that it is
 * wedged, and the lock is poisoned rather than holding every query behind it.
 */
const RESTORE_HOLD_DEADLINE_MS = 180_000;

/**
 * How long a backup capture waits in line for the connection slot. Capturing is consistent without
 * the slot (the native engine checks the WAL header around its copy); the slot only removes the
 * rare retry. A busy or stuck engine is exactly when a backup must not fail, so past this the
 * capture runs without it.
 */
const BACKUP_CAPTURE_SLOT_WAIT_MS = 5000;

/**
 * Reading every row of a study database for a web backup, in one hold. Large decks read in well
 * under a second; past a minute the connection is wedged.
 */
const BACKUP_SNAPSHOT_HOLD_DEADLINE_MS = 60_000;

/**
 * Closing may legitimately wait behind the longest holder (a pull apply) without being skipped —
 * a skipped close never runs at all. The close's CALLER is bounded separately (the close-safe
 * wrapper and the lifecycle); this only keeps the close attempt itself in line until the holder
 * finishes or the lock is poisoned.
 */
const CLOSE_WAIT_DEADLINE_MS = HOLD_DEADLINE_MS["sync-apply"] + 5000;
// What an app statement may not do on a synced replica, matched on the statement's own shape (its
// leading keyword and its target), never on words that may appear in a value or a column name.
const LEADING_COMMENTS = /^(?:\s+|--[^\n]*(?:\n|$)|\/\*[\s\S]*?\*\/)+/;
const SCHEMA_STATEMENT = /^(?:CREATE|ALTER|DROP|REINDEX|VACUUM|ATTACH|DETACH)\b/i;
const SCHEMA_PRAGMA =
  /^PRAGMA\s+(?:\w+\.)?(?:writable_schema|(?:unstable_)?capture_data_changes_conn)\b/i;
// The journal, the engine's cursors and the server's own tables. The web write receipts are not
// here: the leader's ledger is their one writer and writes them through this adapter.
const BOOKKEEPING_WRITE =
  /^(?:INSERT\s+(?:OR\s+\w+\s+)?INTO|REPLACE\s+INTO|UPDATE(?:\s+OR\s+\w+)?|DELETE\s+FROM)\s+["'`[]?(?:__drizzle_migrations|turso_sync_last_change_id|haniverse_sync_\w+)\b/i;

/**
 * An app statement tried to change the schema or the sync bookkeeping of a synced replica. Both
 * have exactly one writer (the schema updater and the engine); see `update-actor-schema.ts`. This
 * is a programming error, reported where it happens instead of surfacing later as a set-aside
 * upload.
 */
export class SyncedSchemaWriteError extends Error {
  override readonly name = "SyncedSchemaWriteError";
  constructor(statement: string) {
    super(
      `App statements may not change a synced database's schema or sync bookkeeping: ${statement.slice(0, 120)}`,
    );
  }
}

function writesSyncedSchema(statement: string): boolean {
  const sql = statement.replace(LEADING_COMMENTS, "");
  return SCHEMA_STATEMENT.test(sql) || SCHEMA_PRAGMA.test(sql) || BOOKKEEPING_WRITE.test(sql);
}

/** Closing a connection whose lock was poisoned, on an engine that cannot drain safely by itself. */
export class ConnectionAbandonedError extends Error {
  constructor(databaseName: string, cause: unknown) {
    super(
      `${databaseName} was abandoned without a native close: an earlier operation never finished and may still be using it`,
      { cause },
    );
    this.name = "ConnectionAbandonedError";
  }
}

export type TursoStatementCompleted = (source: string, isScript: boolean) => Promise<void>;

export class TursoExpoSqliteAdapter {
  async executeBackupRestore(
    statements: ActorBackupRestoreStatement[],
    options?: ActorBackupRestoreOptions,
  ): Promise<void> {
    await this.lock.runExclusive(
      async () => {
        if (!this.lock.recoveryHold) {
          throw new Error("Restore requires cloud sync to be paused");
        }
        // Same hold as the replacement: nothing can be written between this safety backup and it.
        await options?.checkpoint?.();
        // Bypass this adapter's ordinary transaction tracking. Other statements
        // must wait on the connection lock, never join this transaction.
        await this.tursoDb.exec("BEGIN IMMEDIATE");
        try {
          for (const statement of statements) {
            await this.tursoDb.run(statement.sql, ...normalizeParams(statement.params));
          }
          await this.tursoDb.exec("COMMIT");
        } catch (error) {
          try {
            await this.tursoDb.exec("ROLLBACK");
          } catch (rollbackError) {
            this.lock.poison(rollbackError);
            throw new AggregateError([error, rollbackError], "Restore and rollback failed", {
              cause: rollbackError,
            });
          }
          throw error;
        }
      },
      {
        holdDeadlineMs: RESTORE_HOLD_DEADLINE_MS,
        kind: "statement",
        queryText: "restore local backup",
      },
    );
  }
  private readonly backupConnectionId = `${Date.now()}-${Math.random()}`;
  getBackupSnapshotState() {
    return {
      closed: this.lock.closed,
      connection: this.backupConnectionId,
      databasePath: this.databasePath,
      idle: this.lock.idle,
      version: this.lock.version,
    };
  }
  async setBackupRecoveryHold(enabled: boolean): Promise<void> {
    this.lock.recoveryHold = enabled;
    if (enabled) {
      // Uploads use independent CDC readers, so they do not own the app lock.
      // Pause admission first and drain an existing upload before restore can
      // replace the rows it is still scanning. Foreground work remains free.
      await this.lock.drainBackgroundSync();
    }
    // Drain any apply already admitted; future apply checks the hold inside its lock.
    await this.lock.runExclusive(async () => {});
  }

  /**
   * Run a native backup capture holding the connection slot when it can be had within
   * {@link BACKUP_CAPTURE_SLOT_WAIT_MS}, so no app statement or sync apply interleaves with the file
   * copy. Otherwise capture without it: the copy is consistent either way (the native engine
   * brackets it with WAL-header checks), and a backup must not fail because the database is busy.
   */
  async runBackupCapture<T>(capture: () => Promise<T>): Promise<T> {
    let started = false;
    try {
      return await this.lock.runExclusive(
        async () => {
          started = true;
          return capture();
        },
        {
          kind: "statement",
          queryText: "capture local backup",
          waitDeadlineMs: BACKUP_CAPTURE_SLOT_WAIT_MS,
        },
      );
    } catch (error) {
      if (started) {
        throw error;
      }
      return capture();
    }
  }

  /**
   * Every row of `tables` in ONE hold of the connection slot: no app statement or sync apply can
   * land between two reads, so the result is one committed state (web backups).
   */
  async readBackupSnapshot(tables: readonly string[]): Promise<Record<string, unknown[]>> {
    const sources = tables.map((table) => [table, actorBackupTableSql(table)] as const);
    return this.lock.runExclusive(
      async () => {
        const result: Record<string, unknown[]> = {};
        for (const [table, source] of sources) {
          result[table] = await this.tursoDb.all(source);
        }
        return result;
      },
      {
        holdDeadlineMs: BACKUP_SNAPSHOT_HOLD_DEADLINE_MS,
        kind: "statement",
        queryText: "read local backup snapshot",
      },
    );
  }
  public readonly databaseName: string;
  public readonly options: Record<string, unknown> = {};

  // When a drizzle transaction is open we hold the lock slot across its whole
  // `begin` … `commit`; this is the held slot's release (empty when no transaction
  // is open). Pushed by the `begin` frame, popped by `commit`/`rollback`.
  //
  // A STACK, not a single field. As one field, a second `begin` arriving while a
  // transaction was already open overwrote the first release — orphaning that slot
  // with nothing able to release it, so every later acquirer queued forever. That is
  // the wedge that hung every actor query at once and failed all 6 staging web
  // maestro shards in run 33231390463 (each stuck `in-flight`, none progressing).
  //
  // Note the deadlock is reachable even BEFORE the overwrite: a nested `begin` calls
  // `lock.acquire()` while this adapter already holds the only slot, so it waits on
  // itself. `isInTransaction()` is what keeps a nested `begin` from re-acquiring.
  private readonly txReleases: LockRelease[] = [];

  constructor(
    private readonly tursoDb: TursoDatabase,
    public readonly databasePath: string,
    databaseName: string,
    private readonly lock: ActorSyncConnectionLock,
    private readonly statementCompleted?: TursoStatementCompleted,
    private readonly enforceTransactionOwnership = true,
    private readonly scope?: {
      active: boolean;
      pending: Set<Promise<unknown>>;
      transactionActive: boolean;
    },
    /** A synced replica: app statements may not change its schema or sync bookkeeping. */
    private readonly protectsSyncedSchema = false,
  ) {
    this.databaseName = databaseName;
  }

  private closing = false;
  private closePromise?: Promise<void>;

  /** Only the callback's connection may bypass the held transaction lock. */
  withTransactionScope = <T>(operation: (connection: SQLiteDatabase) => Promise<T>): Promise<T> => {
    if (this.closing || this.scope !== undefined) {
      return Promise.reject(new Error("Invalid transaction owner"));
    }
    return this.lock.runExclusive(
      async () => {
        const scope = {
          active: true,
          pending: new Set<Promise<unknown>>(),
          transactionActive: false,
        };
        const scoped = new TursoExpoSqliteAdapter(
          this.tursoDb,
          this.databasePath,
          this.databaseName,
          this.lock,
          this.statementCompleted,
          true,
          scope,
          this.protectsSyncedSchema,
        );
        // Session.transaction owns BEGIN/COMMIT/ROLLBACK; prevent it from recursively acquiring.
        Reflect.set(scoped, "withTransactionScope", undefined);
        try {
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- adapter implements the SQLiteDatabase surface used by transaction callers
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- adapter implements the SQLiteDatabase surface used by transaction callers
          return await operation(scoped as unknown as SQLiteDatabase);
        } finally {
          scope.active = false;
          await Promise.allSettled(scope.pending);
          if (scope.transactionActive) {
            await this.rollbackAbandonedTransaction();
          }
        }
      },
      { kind: "begin", queryText: "owned transaction callback" },
    );
  };

  /** True while a drizzle transaction is open on this connection. */
  isInTransaction(): boolean {
    return this.txReleases.length > 0;
  }

  /** Pop one transaction frame and release whatever slot it held. */
  private popTransactionFrame(): void {
    this.txReleases.pop()?.();
  }

  /**
   * Release the connection slot for a transaction that will never issue its
   * `commit`/`rollback`.
   *
   * The engine's own docs list this as a named leak trigger: a body abandoned by an
   * unmount, a `cancelQueries()`, or a navigation mid-transaction issues neither
   * frame, so the slot is held forever and every subsequent read/write on this
   * connection queues behind it — the app stays browsable while one screen spins,
   * which is exactly how this presented on staging. `close()` calls this, and a
   * caller that knowingly abandons a transaction should too.
   *
   * Releases EVERY held frame (a nested transaction stacks several) and is safe to
   * call when nothing is open. The underlying releases are idempotent, so racing a
   * real `commit` is harmless.
   */
  abandonOpenTransaction(): void {
    while (this.txReleases.length > 0) {
      this.popTransactionFrame();
    }
  }

  /** A scope ended with its transaction still open: undo it, or give the connection up. */
  private async rollbackAbandonedTransaction(): Promise<void> {
    try {
      await this.tursoDb.exec("ROLLBACK");
    } catch (error) {
      this.lock.poison(error);
      throw error;
    }
  }

  /**
   * Run `op` under the connection lock with drizzle-transaction framing.
   *
   * - `begin` ACQUIRES the slot and stashes its release on the instance, so every
   *   statement in the transaction body — and the eventual `commit`/`rollback` —
   *   runs while the slot is held. A `pull()`/`push()` therefore cannot interleave
   *   between `begin` and `commit`.
   * - `commit`/`rollback` run `op` then RELEASE the held slot.
   * - Any other statement: if a transaction is open (`isInTransaction()`) it runs
   *   directly under the already-held slot (re-acquiring would self-deadlock);
   *   otherwise it acquires the slot just for this one statement.
   *
   * Savepoint statements (drizzle's nested transactions) classify as `null` and
   * always run inside an outer transaction's held slot, so they're handled by the
   * "transaction open → run directly" branch.
   */
  private async runLocked<T>(source: string, op: () => Promise<T>, isScript = false): Promise<T> {
    // A multi-statement `execAsync` script can start with `BEGIN` and end with `COMMIT`, so
    // classifying only its first token would retain the transaction frame after the script already closed the
    // transaction. A one-statement exec is also how drizzle sends its separate `begin` / `commit`
    // frames, though, and those MUST keep the lock held between calls. Classify that single
    // statement normally while treating a complete multi-statement script as one exclusive slot.
    if (this.closing || (this.scope !== undefined && !this.scope.active)) {
      throw new Error("Database connection or transaction scope has ended");
    }
    const statements = isScript ? splitSqlStatements(source) : [source];
    const control =
      statements.length === 1 ? classifyTransactionControl(statements[0] ?? source) : null;
    if (this.protectsSyncedSchema) {
      const refused = statements.find((statement) => writesSyncedSchema(statement));
      if (refused !== undefined) {
        throw new SyncedSchemaWriteError(refused);
      }
    }
    const runWithCompletion = async (): Promise<T> => {
      const result = await op();
      if (this.scope !== undefined) {
        if (control === "begin") {
          this.scope.transactionActive = true;
        } else if (control === "commit" || control === "rollback") {
          this.scope.transactionActive = false;
        }
      }
      await this.statementCompleted?.(source, isScript);
      return result;
    };
    if (this.scope !== undefined) {
      const operation = runWithCompletion();
      const pending = this.scope.pending;
      pending.add(operation);
      void operation.then(
        () => pending.delete(operation),
        () => pending.delete(operation),
      );
      return operation;
    }
    if (
      this.enforceTransactionOwnership &&
      statements.some((statement) => requiresTransactionOwner(statement))
    ) {
      throw new Error("Transaction control requires an owned transaction callback");
    }
    if (control === "begin") {
      // A NESTED `begin` must NOT re-acquire: this adapter already holds the single
      // slot, so acquiring again would wait on itself forever (a self-deadlock, not
      // just a leak). Push a no-op frame so the matching `commit`/`rollback` pops
      // symmetrically and the outermost frame still owns the real release.
      if (this.isInTransaction()) {
        this.txReleases.push(() => {});
        return await runWithCompletion();
      }
      // Acquire and HOLD across the transaction. If the body or commit throws, the
      // commit/rollback frame still releases; but guard the acquire→run window so a
      // failure to run `begin` itself doesn't leak the slot.
      // Label the holder so a leaked slot reports as a `begin` that never got its
      // commit — the one holder kind that spans statements and can therefore leak.
      const release = await this.lock.acquire({ kind: "begin", queryText: source });
      this.txReleases.push(release);
      try {
        return await runWithCompletion();
      } catch (error) {
        this.popTransactionFrame();
        throw error;
      }
    }
    if (control === "commit" || control === "rollback") {
      // Pop BEFORE running so the statement itself sees no open transaction, and so a
      // throw from `commit` still releases (the `finally` below).
      const release = this.txReleases.pop();
      try {
        return await runWithCompletion();
      } finally {
        release?.();
      }
    }
    // Plain statement. Inside an open transaction it runs under the held slot;
    // otherwise it gets its own short-lived slot.
    if (this.isInTransaction()) {
      return await runWithCompletion();
    }
    return this.lock.runExclusive(runWithCompletion, { kind: "statement", queryText: source });
  }

  async runAsync(
    source: string,
    ...rest: unknown[]
  ): Promise<{ changes: number; lastInsertRowId: number }> {
    const params = normalizeParams(hasLength(rest, 1) ? rest[0] : rest);
    return this.runLocked(source, async () => {
      const result = await this.tursoDb.run(source, ...params);
      return { changes: result.changes, lastInsertRowId: result.lastInsertRowid };
    });
  }

  async getAllAsync<T = unknown>(source: string, ...rest: unknown[]): Promise<T[]> {
    const params = normalizeParams(hasLength(rest, 1) ? rest[0] : rest);
    return this.runLocked(source, async () => {
      const rows = await this.tursoDb.all(source, ...params);
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- row shape comes from the caller's query
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- row shape comes from the caller's query
      return rows as T[];
    });
  }

  async getFirstAsync<T = unknown>(source: string, ...rest: unknown[]): Promise<T | null> {
    const params = normalizeParams(hasLength(rest, 1) ? rest[0] : rest);
    return this.runLocked(source, async () => {
      const row = await this.tursoDb.get(source, ...params);
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- row shape comes from the caller's query
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- row shape comes from the caller's query
      return (row ?? null) as T | null;
    });
  }

  async execAsync(source: string): Promise<void> {
    return this.runLocked(
      source,
      async () => {
        await this.tursoDb.exec(source);
      },
      true,
    );
  }

  prepareAsync(source: string): Promise<TursoAdapterStatement> {
    const tursoDb = this.tursoDb;
    const runLocked = <T>(op: () => Promise<T>): Promise<T> => this.runLocked(source, op);
    const statement: TursoAdapterStatement = {
      async executeAsync(params?: unknown) {
        const bind = normalizeParams(params);
        return runLocked(async () => {
          const result = await tursoDb.run(source, ...bind);
          return {
            changes: result.changes,
            // The sub-readers run within the same already-held slot (this method's
            // `runLocked` slot is released by the time they're awaited, but they
            // re-acquire on their own — they're standalone reads).
            getAllAsync: async () => runLocked(() => tursoDb.all(source, ...bind)),
            getFirstAsync: async () =>
              runLocked(async () => (await tursoDb.get(source, ...bind)) ?? null),
            lastInsertRowId: result.lastInsertRowid,
          };
        });
      },
      async executeForRawResultAsync<T>(params?: unknown) {
        const bind = normalizeParams(params);
        return runLocked(async () => {
          const rows = await tursoDb.all(source, ...bind);
          // drizzle's `.values()` path needs positional row tuples. Turso returns
          // name-keyed objects whose key insertion order is the column order
          // (`Statement.readRow` writes columns 0..n in order), so `Object.values`
          // yields the correct positional tuple.
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- tuple shape matches the caller's column projection
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- tuple shape matches the caller's column projection
          const tuples = rows.map((row) => Object.values(row as Record<string, unknown>)) as T[];
          return { getAllAsync: async () => tuples };
        });
      },
      async finalizeAsync() {
        // Turso `Database.run/all/get` finalize their own prepared statements;
        // nothing to release here.
      },
    };
    return Promise.resolve(statement);
  }

  // NB: the drizzle `expo-sqlite-async` session implements transactions itself by
  // issuing `begin`/`commit`/`rollback` as plain SQL through `runAsync`, so no
  // connection-level transaction method is needed here. `runLocked` frames those
  // statements so the whole transaction holds ONE lock slot — see its doc comment.

  closeAsync(): Promise<void> {
    if (this.scope !== undefined) {
      return Promise.reject(new Error("A transaction cannot close its database owner"));
    }
    this.closing = true;
    if (!this.enforceTransactionOwnership) {
      this.abandonOpenTransaction();
    }
    const attempt = (this.closePromise ??= this.closeEngine());
    // A failed close can be retried: forget it so the next call makes a new attempt.
    void attempt.catch(() => {
      if (this.closePromise === attempt) {
        this.closePromise = undefined;
      }
    });
    return attempt;
  }

  private async closeEngine(): Promise<void> {
    try {
      await this.lock.runExclusive(
        async () => {
          this.lock.invalidate();
          await this.tursoDb.close();
        },
        {
          kind: "statement",
          queryText: "close connection",
          waitDeadlineMs: CLOSE_WAIT_DEADLINE_MS,
        },
      );
    } catch (error) {
      if (!this.lock.damaged) {
        throw error;
      }
      // Poisoned: a holder overran and may still be running on this engine, so the slot will
      // never come back. Stop admitting work either way; close natively only when the engine
      // waits for its own in-flight calls first — otherwise abandon it (never interleave).
      this.lock.invalidate();
      if (Reflect.get(this.tursoDb, DRAINS_IN_FLIGHT_ON_CLOSE) === true) {
        await this.tursoDb.close();
        return;
      }
      throw new ConnectionAbandonedError(this.databaseName, error);
    }
  }
}

export function adaptTursoToExpoSqlite(
  tursoDb: TursoDatabase,
  databasePath: string,
  databaseName: string,
  lock: ActorSyncConnectionLock,
  statementCompleted?: TursoStatementCompleted,
  enforceTransactionOwnership = false,
  protectsSyncedSchema = false,
): SQLiteDatabase {
  const adapter = new TursoExpoSqliteAdapter(
    tursoDb,
    databasePath,
    databaseName,
    lock,
    statementCompleted,
    enforceTransactionOwnership,
    undefined,
    protectsSyncedSchema,
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- adapter implements the expo-sqlite surface the codebase calls
  return adapter as unknown as SQLiteDatabase;
}
